import * as React from 'react';
import { SharePointFolder } from '../../../types';
import './FolderTree.css';

interface FolderTreeProps {
  nodes: SharePointFolder[];
  selectedPath: string;
  expandedPaths: Set<string>;
  rootNode?: SharePointFolder;
  disabled?: boolean;
  pendingParent?: string | null;
  pendingName?: string;
  pendingError?: string;
  isPendingCreating?: boolean;
  renamingPath?: string | null;
  renameName?: string;
  renameError?: string;
  isRenaming?: boolean;
  isRefreshing?: boolean;
  onRefresh?: () => Promise<void> | void;
  onSelect: (node: SharePointFolder) => void;
  onToggle: (path: string) => void;
  onPendingNameChange?: (name: string) => void;
  onPendingSubmit?: () => void;
  onPendingCancel?: () => void;
  onRenameNameChange?: (name: string) => void;
  onRenameSubmit?: () => void;
  onRenameCancel?: () => void;
  onRowContextMenu?: (e: React.MouseEvent, parentPath: string) => void;
}

interface FlatNode {
  node: SharePointFolder;
  depth: number;
  hasChildren: boolean;
  isExpanded: boolean;
  isPending?: boolean;
}

const PENDING_PATH = '__pending_new_folder__';

function flattenVisible(
  nodes: SharePointFolder[],
  expandedPaths: Set<string>,
  depth: number,
  out: FlatNode[]
): void {
  for (const node of nodes) {
    const hasChildren = !!node.children?.length;
    const isExpanded = expandedPaths.has(node.path);
    out.push({ node, depth, hasChildren, isExpanded });
    if (hasChildren && isExpanded) {
      flattenVisible(node.children!, expandedPaths, depth + 1, out);
    }
  }
}

const PendingRow: React.FC<{
  depth: number;
  name: string;
  error: string;
  isCreating: boolean;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}> = ({ depth, name, error, isCreating, onChange, onSubmit, onCancel }) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const cancelledRef = React.useRef(false);

  React.useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  return (
    <>
      <div
        className="folder-tree-row folder-tree-row--pending"
        style={{ paddingLeft: 8 + depth * 16 }}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="folder-tree-chevron-spacer" />
        <span className="material-icons-round folder-tree-icon">folder</span>
        <input
          ref={inputRef}
          type="text"
          className="folder-tree-pending-input"
          value={name}
          disabled={isCreating}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSubmit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancelledRef.current = true;
              onCancel();
            }
            e.stopPropagation();
          }}
          onBlur={() => {
            if (cancelledRef.current) {
              cancelledRef.current = false;
              return;
            }
            onSubmit();
          }}
        />
      </div>
      {error && (
        <div
          className="folder-tree-pending-error"
          style={{ paddingLeft: 8 + depth * 16 + 22 }}
        >
          {error}
        </div>
      )}
    </>
  );
};

const FolderTree: React.FC<FolderTreeProps> = ({
  nodes,
  selectedPath,
  expandedPaths,
  rootNode,
  disabled = false,
  pendingParent,
  pendingName = '',
  pendingError = '',
  isPendingCreating = false,
  renamingPath,
  renameName = '',
  renameError = '',
  isRenaming = false,
  isRefreshing = false,
  onRefresh,
  onSelect,
  onToggle,
  onPendingNameChange,
  onPendingSubmit,
  onPendingCancel,
  onRenameNameChange,
  onRenameSubmit,
  onRenameCancel,
  onRowContextMenu,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const flat: FlatNode[] = [];
  if (rootNode) {
    flat.push({ node: rootNode, depth: 0, hasChildren: false, isExpanded: false });
  }
  flattenVisible(nodes, expandedPaths, rootNode ? 1 : 0, flat);

  if (pendingParent !== null && pendingParent !== undefined) {
    const parentIdx = flat.findIndex((item) => item.node.path === pendingParent);
    if (parentIdx !== -1) {
      const parentDepth = flat[parentIdx].depth;
      let insertIdx = parentIdx + 1;
      while (insertIdx < flat.length && flat[insertIdx].depth > parentDepth) {
        insertIdx++;
      }
      flat.splice(insertIdx, 0, {
        node: { name: '', path: PENDING_PATH, children: [] },
        depth: parentDepth + 1,
        hasChildren: false,
        isExpanded: false,
        isPending: true,
      });
    }
  }

  const focusRow = (index: number) => {
    if (index < 0 || index >= flat.length) return;
    const path = flat[index].node.path;
    const root = containerRef.current;
    if (!root) return;
    const el = root.querySelector<HTMLDivElement>(
      `.folder-tree-row[data-path="${CSS.escape(path)}"]`
    );
    el?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    if (disabled) return;
    const { node, hasChildren, isExpanded } = flat[index];
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusRow(index + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusRow(index - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (hasChildren && !isExpanded) {
          onToggle(node.path);
        } else if (hasChildren && isExpanded) {
          focusRow(index + 1);
        }
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (hasChildren && isExpanded) {
          onToggle(node.path);
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        onSelect(node);
        break;
    }
  };

  if (flat.length === 0) {
    return (
      <div className="folder-tree folder-tree--empty">
        <span className="material-icons-round">folder_off</span>
        <span>Geen mappen gevonden.</span>
      </div>
    );
  }

  return (
    <div className="folder-tree" role="tree" ref={containerRef}>
      {flat.map((item, index) => {
        const { node, depth, hasChildren, isExpanded, isPending } = item;

        if (isPending) {
          return (
            <PendingRow
              key={PENDING_PATH}
              depth={depth}
              name={pendingName}
              error={pendingError}
              isCreating={isPendingCreating}
              onChange={(v) => onPendingNameChange?.(v)}
              onSubmit={() => onPendingSubmit?.()}
              onCancel={() => onPendingCancel?.()}
            />
          );
        }

        const isSelected = node.path === selectedPath;
        const isRoot = rootNode != null && index === 0;
        const isRenamingThis = renamingPath != null && renamingPath === node.path;
        return (
          <div
            key={isRoot ? '__root__' : node.path}
            role="treeitem"
            aria-expanded={hasChildren ? isExpanded : undefined}
            aria-selected={isSelected}
            aria-level={depth + 1}
            tabIndex={isSelected || (selectedPath === '' && index === 0) ? 0 : -1}
            data-path={node.path}
            className={`folder-tree-row ${isSelected ? 'is-selected' : ''} ${isRoot ? 'folder-tree-row--root' : ''}`}
            style={{ paddingLeft: 8 + depth * 16 }}
            onClick={() => !disabled && !isRenamingThis && onSelect(node)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onContextMenu={(e) => {
              if (disabled || !onRowContextMenu) return;
              onRowContextMenu(e, node.path);
            }}
          >
            {hasChildren ? (
              <button
                type="button"
                className="folder-tree-chevron"
                tabIndex={-1}
                aria-label={isExpanded ? 'Inklappen' : 'Uitklappen'}
                disabled={disabled || isRenamingThis}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(node.path);
                }}
              >
                <span className="material-icons-round">
                  {isExpanded ? 'expand_more' : 'chevron_right'}
                </span>
              </button>
            ) : (
              <span className="folder-tree-chevron-spacer" />
            )}
            <span className="material-icons-round folder-tree-icon">
              {isRoot ? 'home' : 'folder'}
            </span>
            {isRenamingThis ? (
              <RenameInput
                value={renameName}
                error={renameError}
                disabled={isRenaming}
                onChange={(v) => onRenameNameChange?.(v)}
                onSubmit={() => onRenameSubmit?.()}
                onCancel={() => onRenameCancel?.()}
              />
            ) : (
              <span className="folder-tree-label">{node.name}</span>
            )}
            {isRoot && onRefresh && (
              <button
                type="button"
                className="folder-tree-refresh"
                tabIndex={-1}
                aria-label="Vernieuwen"
                title="Vernieuwen"
                disabled={isRefreshing}
                onClick={(e) => {
                  e.stopPropagation();
                  onRefresh();
                }}
              >
                <span
                  className={`material-icons-round${isRefreshing ? ' folder-tree-refresh-icon--spinning' : ''}`}
                >
                  refresh
                </span>
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const RenameInput: React.FC<{
  value: string;
  error: string;
  disabled: boolean;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}> = ({ value, error, disabled, onChange, onSubmit, onCancel }) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const cancelledRef = React.useRef(false);

  React.useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  return (
    <span className="folder-tree-rename-wrap" onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        type="text"
        className="folder-tree-pending-input"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelledRef.current = true;
            onCancel();
          }
          e.stopPropagation();
        }}
        onBlur={() => {
          if (cancelledRef.current) {
            cancelledRef.current = false;
            return;
          }
          onSubmit();
        }}
      />
      {error && <span className="folder-tree-pending-error">{error}</span>}
    </span>
  );
};

export default FolderTree;
